import get from "lodash/get";
import { array, bool, func, number, object, string } from "prop-types";
import { useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { getCategorySubCategory, getPublishedDate, getPublishedTime } from "../../utils";
import { TwitterTweetEmbed } from "react-twitter-embed";
import "./aihub.m.css";
import { SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../../arrow";
import { isDesktop } from "react-device-detect";

const FIRST_PRINCIPLES_TARGET = "first_principles";
const CLOSE_AI_HUB_EVENT = "fortune:close-aihub";
const AI_HUB_SCROLL_SECTION_VISIBILITY_RATIO = 0.5;
const AI_HUB_SCROLL_SECTION_MIN_TIME_MS = 100;
const AI_HUB_QUERY_AUTO_OPEN_FLAG = "__fortuneAiHubQueryAutoOpened";

const getAiHubPageUrl = (storyUrl) => {
  if (typeof window === "undefined") {
    return storyUrl || "";
  }

  try {
    const url = new URL(storyUrl || window.location.href, window.location.origin);

    url.searchParams.set("aihub", "true");
    return url.toString();
  } catch (error) {
    const currentUrl = storyUrl || window.location.href;
    const separator = currentUrl.includes("?") ? "&" : "?";

    return `${currentUrl}${separator}aihub=true`;
  }
};

const getStoryContentId = (story = {}) => get(story, ["story-content-id"], "") || get(story, ["id"], "");

const formatPublishTime = (publishTime) => {
  if (!publishTime) {
    return "";
  }

  const date = new Date(publishTime);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
    timeZoneName: "short",
  }).format(date);

  return `${datePart}, ${timePart}`;
};

const AIHub = ({
  aiHubClick,
  setAiHubClick,
  aiHubScrollTarget,
  headline,
  publishTime,
  heroImageS3Key,
  summary,
  topic,
  keyTakeaways,
  timeline,
  faqs,
  glossary,
  isLoading,
  hasSummary,
  hasTopic,
  hasKeyTakeaways,
  hasTimeline,
  hasFaqs,
  hasGlossary,
  hasTweets,
  tweets,
  story,
  qtConfig,
  aiHubTemplate,
  shouldShowAiHubButton,
  shouldAutoOpenAiHub,
  hasInTheStory,
  inTheStory,
  storyInstanceId,
}) => {
  const cardsRef = useRef([]);
  const contentRef = useRef(null);
  const firstPrinciplesRef = useRef(null);
  const scrollSectionRefs = useRef({});
  const visibleScrollSectionsRef = useRef(new Set());
  const trackedScrollSectionsRef = useRef(new Set());
  const scrollSectionTimersRef = useRef({});
  const aiHubScrollStateRef = useRef({ direction: "down", hasScrolledDown: false, lastScrollTop: 0 });
  const isAiHubScrollTrackingOpenRef = useRef(false);
  const wasAiHubOpenRef = useRef(false);
  const trackedAiHubPageViewRef = useRef(false);
  const trackedAiHubAutoOpenRef = useRef(false);
  const [aiHubshowPopup, setAiHubshowPopup] = useState(false);
  const [statList, setStatList] = useState(false);
  const [openFaqIndexes, setOpenFaqIndexes] = useState([]);
  const normalizedTakeaways = Array.isArray(keyTakeaways) ? keyTakeaways.filter(Boolean) : [];
  const normalizedTimeline = Array.isArray(timeline)
    ? timeline.filter((item) => get(item, ["date"]) || get(item, ["event"]))
    : [];
  const normalizedFaqs = Array.isArray(faqs)
    ? faqs.filter((item) => get(item, ["question"]) && get(item, ["answer"]))
    : [];
  const normalizedGlossary = Array.isArray(glossary)
    ? glossary.filter((item) => get(item, ["term"]) && get(item, ["definition"]))
    : [];

  const normalizedInTheStory = Array.isArray(inTheStory)
    ? inTheStory.filter((item) => get(item, ["name"]) && get(item, ["rankLabel"]) && get(item, ["href"]) && get(item, ["rankYear"]))
    : [];
  const normalizedTweets = Array.isArray(tweets) ? tweets.filter((item) => get(item, ["id"])) : [];
  const articleTitle = topic || headline;
  const publishedLabel = formatPublishTime(publishTime);
  const qtStates = useSelector((state) => state);
  const qtState = get(qtStates, ["qt"], {});
  const isWebView = get(qtState, ["config", "isWebView"]) === true;
  const hasAiHubContent =
    (hasSummary && Boolean(summary)) ||
    (hasTopic && Boolean(topic)) ||
    (hasKeyTakeaways && normalizedTakeaways.length > 0) ||
    (hasTimeline && normalizedTimeline.length > 0) ||
    (hasFaqs && normalizedFaqs.length > 0) ||
    (hasGlossary && normalizedGlossary.length > 0) ||
    (hasInTheStory && normalizedInTheStory.length > 0) ||
    (hasTweets && normalizedTweets.length > 0);
  const isAiHubOpen = aiHubClick || (aiHubshowPopup && shouldShowAiHubButton);

  useEffect(() => {
    const wasAiHubOpen = wasAiHubOpenRef.current;
    wasAiHubOpenRef.current = isAiHubOpen;

    if (!isAiHubOpen || wasAiHubOpen || aiHubScrollTarget === FIRST_PRINCIPLES_TARGET) {
      return;
    }

    contentRef.current?.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [aiHubScrollTarget, isAiHubOpen]);

  useEffect(() => {
    if (!isAiHubOpen || aiHubScrollTarget !== FIRST_PRINCIPLES_TARGET) {
      return undefined;
    }

    let timeoutId;
    let attempts = 0;

    const scrollToFirstPrinciples = () => {
      const contentElement = contentRef.current;
      const firstPrinciplesElement = firstPrinciplesRef.current;

      if (!contentElement || !firstPrinciplesElement) {
        if (attempts < 30) {
          attempts += 1;
          timeoutId = setTimeout(scrollToFirstPrinciples, 100);
        }
        return;
      }

      const contentRect = contentElement.getBoundingClientRect();
      const firstPrinciplesRect = firstPrinciplesElement.getBoundingClientRect();
      const scrollOffset = 16;

      if ((contentRect.height === 0 || firstPrinciplesRect.height === 0) && attempts < 30) {
        attempts += 1;
        timeoutId = setTimeout(scrollToFirstPrinciples, 100);
        return;
      }

      const scrollTop = contentElement.scrollTop + firstPrinciplesRect.top - contentRect.top - scrollOffset;

      contentElement.scrollTo({
        top: Math.max(scrollTop, 0),
        behavior: "smooth",
      });
    };

    timeoutId = setTimeout(scrollToFirstPrinciples, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [aiHubScrollTarget, isAiHubOpen, normalizedGlossary.length]);

  useEffect(() => {
    if (!isAiHubOpen || !aiHubScrollTarget || aiHubScrollTarget === FIRST_PRINCIPLES_TARGET) {
      return undefined;
    }

    let timeoutId;
    let attempts = 0;

    const scrollToTargetSection = () => {
      const contentElement = contentRef.current;
      const targetElement = scrollSectionRefs.current[aiHubScrollTarget];

      if (!contentElement || !targetElement) {
        if (attempts < 30) {
          attempts += 1;
          timeoutId = setTimeout(scrollToTargetSection, 100);
        }
        return;
      }

      const contentRect = contentElement.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const scrollOffset = 16;

      if ((contentRect.height === 0 || targetRect.height === 0) && attempts < 30) {
        attempts += 1;
        timeoutId = setTimeout(scrollToTargetSection, 100);
        return;
      }

      const scrollTop = contentElement.scrollTop + targetRect.top - contentRect.top - scrollOffset;

      contentElement.scrollTo({
        top: Math.max(scrollTop, 0),
        behavior: "smooth",
      });
    };

    timeoutId = setTimeout(scrollToTargetSection, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [aiHubScrollTarget, isAiHubOpen, normalizedTimeline.length]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const pageWrapper = document.querySelector(".page-wrapper");
    if (!pageWrapper) {
      return undefined;
    }

    pageWrapper.classList.toggle("aihub", isAiHubOpen);

    return () => {
      pageWrapper.classList.remove("aihub");
    };
  }, [isAiHubOpen]);

  useEffect(() => {
    if (!isAiHubOpen || typeof window === "undefined") {
      trackedAiHubPageViewRef.current = false;
      return;
    }

    if (trackedAiHubPageViewRef.current) {
      return;
    }

    trackedAiHubPageViewRef.current = true;

    const { category, subCategory } = getCategorySubCategory(story?.slug);
    const storyTitle = story?.headline || headline || "";

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: story?.["assignee-name"],
      byline: story?.authors?.map((author) => author.name).toString(),
      category: category || "NA",
      sub_category: subCategory || "NA",
      content_id: story?.["public-identifier"] || story?.["sequence-no"] || "NA",
      template_name: "aihub",
      publish_date: getPublishedDate(story?.["published-at"]),
      publish_time: getPublishedTime(story?.["published-at"]),
      login_status: "not-logged-in",
      subscription_status: "not-subscribed",
      ad_free: false,
      login_wall_enabled: story?.["story-template"] === "magazine",
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
      url: getAiHubPageUrl(story?.url),
      page_title: `AI HUB - ${storyTitle}`,
    });
  }, [headline, isAiHubOpen, story]);

  useEffect(() => {
    setOpenFaqIndexes(normalizedFaqs.map((_, index) => index));
  }, [aiHubClick, normalizedFaqs.length]);

  useEffect(() => {
    if (!shouldAutoOpenAiHub || typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const isAihub = params.get("aihub");
    if (isAihub === "true" && !window[AI_HUB_QUERY_AUTO_OPEN_FLAG]) {
      window[AI_HUB_QUERY_AUTO_OPEN_FLAG] = true;
      setAiHubshowPopup(true);
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.classList.add("no-scroll-forSummary");

      if (!trackedAiHubAutoOpenRef.current) {
        trackedAiHubAutoOpenRef.current = true;
        trackAiHubInteraction("open", "open");
      }
    }
  }, [shouldAutoOpenAiHub]);

  function trackAiHubInteraction(eventAction, linkText) {
    if (typeof window === "undefined") {
      return;
    }

    const pageType = "story-page";
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "aihub_interactions",
      event_name: "aihub_interactions",
      event_action: eventAction,
      link_text: linkText,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: "aihub",
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  }

  const handleAiHubClose = () => {
    trackAiHubInteraction("close", "close");
    setAiHubClick(false);
    setAiHubshowPopup(false);
    document.body.classList.remove("no-scroll-forSummary");
    document.body.style.position = "";
    document.body.style.width = "";
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(CLOSE_AI_HUB_EVENT, {
          detail: {
            storyContentId: get(story, ["story-content-id"], "") || get(story, ["id"], ""),
            fallbackStoryContentId: getStoryContentId(story),
            storyInstanceId,
          },
        })
      );
    }
  };

  const handleEntityClick = () => {
    trackAiHubInteraction("entity_click", "entity_click");
  };

  const setAiHubScrollSectionRef = (sectionName) => (element) => {
    if (element) {
      scrollSectionRefs.current[sectionName] = element;
      return;
    }

    delete scrollSectionRefs.current[sectionName];
  };

  useEffect(() => {
    const clearScrollSectionTimers = () => {
      Object.values(scrollSectionTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      scrollSectionTimersRef.current = {};
    };

    if (!isAiHubOpen || typeof window === "undefined" || !window.IntersectionObserver) {
      clearScrollSectionTimers();
      visibleScrollSectionsRef.current.clear();
      trackedScrollSectionsRef.current.clear();
      isAiHubScrollTrackingOpenRef.current = false;
      return undefined;
    }

    const scrollRoot = contentRef.current;

    if (!scrollRoot) {
      return undefined;
    }

    const shouldResetScrollTracking = !isAiHubScrollTrackingOpenRef.current;

    isAiHubScrollTrackingOpenRef.current = true;
    clearScrollSectionTimers();
    visibleScrollSectionsRef.current.clear();

    if (shouldResetScrollTracking) {
      trackedScrollSectionsRef.current.clear();
      aiHubScrollStateRef.current = {
        direction: "down",
        hasScrolledDown: false,
        lastScrollTop: scrollRoot.scrollTop,
      };
    }

    const scheduleScrollSectionEvent = (sectionName) => {
      if (
        trackedScrollSectionsRef.current.has(sectionName) ||
        scrollSectionTimersRef.current[sectionName] ||
        aiHubScrollStateRef.current.direction !== "down" ||
        !aiHubScrollStateRef.current.hasScrolledDown
      ) {
        return;
      }

      scrollSectionTimersRef.current[sectionName] = setTimeout(() => {
        delete scrollSectionTimersRef.current[sectionName];

        if (
          visibleScrollSectionsRef.current.has(sectionName) &&
          !trackedScrollSectionsRef.current.has(sectionName) &&
          aiHubScrollStateRef.current.direction === "down" &&
          aiHubScrollStateRef.current.hasScrolledDown
        ) {
          trackedScrollSectionsRef.current.add(sectionName);
          trackAiHubInteraction("scroll_aihub", sectionName);
        }
      }, AI_HUB_SCROLL_SECTION_MIN_TIME_MS);
    };

    const handleAiHubScroll = () => {
      const currentScrollTop = scrollRoot.scrollTop;
      const lastScrollTop = aiHubScrollStateRef.current.lastScrollTop;

      if (currentScrollTop > lastScrollTop + 1) {
        aiHubScrollStateRef.current.direction = "down";
        aiHubScrollStateRef.current.hasScrolledDown = true;
        visibleScrollSectionsRef.current.forEach((sectionName) => scheduleScrollSectionEvent(sectionName));
      } else if (currentScrollTop < lastScrollTop - 1) {
        aiHubScrollStateRef.current.direction = "up";
      }

      aiHubScrollStateRef.current.lastScrollTop = currentScrollTop;
    };

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionName = entry.target.getAttribute("data-aihub-scroll-section");

          if (!sectionName) {
            return;
          }

          const isSectionVisible =
            entry.isIntersecting && entry.intersectionRatio >= AI_HUB_SCROLL_SECTION_VISIBILITY_RATIO;

          if (isSectionVisible) {
            visibleScrollSectionsRef.current.add(sectionName);
            scheduleScrollSectionEvent(sectionName);
            return;
          }

          visibleScrollSectionsRef.current.delete(sectionName);
          clearTimeout(scrollSectionTimersRef.current[sectionName]);
          delete scrollSectionTimersRef.current[sectionName];
        });
      },
      {
        root: scrollRoot,
        threshold: [0, AI_HUB_SCROLL_SECTION_VISIBILITY_RATIO, 1],
      }
    );

    Object.values(scrollSectionRefs.current).forEach((sectionElement) => {
      if (sectionElement) {
        observer.observe(sectionElement);
      }
    });

    scrollRoot.addEventListener("scroll", handleAiHubScroll, { passive: true });

    return () => {
      observer.disconnect();
      scrollRoot.removeEventListener("scroll", handleAiHubScroll);
      clearScrollSectionTimers();
      visibleScrollSectionsRef.current.clear();
    };
  }, [
    hasFaqs,
    hasGlossary,
    hasTimeline,
    hasTweets,
    isAiHubOpen,
    normalizedFaqs.length,
    normalizedGlossary.length,
    normalizedTimeline.length,
    normalizedTweets.length,
  ]);

  const trackPopupSocialShareClick = (platform) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        story
      );

      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "share_click",
        link_text: platform || "share",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const SocialShareComponent = () => {
    return (
      <div id={`story-share-${story.id}`} styleName="aihub-share-style">
        <SocialShare
          template={(props) => (
            <SocialShareTemplate
              {...props}
              story={story}
              aiHubTemplate={true}
              shareTitle={`Get detailed insights and analysis on Fortune India AI Hub for the story "${story.headline}". Read now:\n\n`}
              qtConfig={qtState.config}
              onShareClick={(platform) => trackPopupSocialShareClick(platform?.toLowerCase())}
            />
          )}
          fullUrl={encodeURI(`${story.url}?aihub=true`)}
          shareTitle={`Get detailed insights and analysis on Fortune India AI Hub for the story "${story.headline}". Read now:`}
          shareIconId={`share-icon-story-share-${story.id}`}
          buttonLabel={"Share"}
        />
      </div>
    );
  };
  return (
    <>
      <div
        styleName={`${aiHubClick ? "open" : ""} ${aiHubshowPopup && shouldShowAiHubButton ? "open" : ""} aihub-overlay`}
        onClick={handleAiHubClose}
      ></div>
      <div styleName={`${aiHubClick ? "open" : ""} ${aiHubshowPopup && shouldShowAiHubButton ? "open" : ""} aihub-popup`}>
        {/*
        <div styleName="aianimation_btn">
          <div styleName="animateelement"></div>
        </div>
        <div styleName="animationInner"></div>
        */}
        <div styleName="aihub-header">
          <div styleName="aihub-title"></div>
          <button styleName="close-aihub-button" aria-label="Close AI Hub" onClick={handleAiHubClose}>
            x
          </button>
          <div styleName="ai-line"></div>
        </div>
        <div className="container">
          <div ref={contentRef} styleName="aihub-content">
            <div styleName="response_aihub">AI Generated by Fortune India</div>
            <div styleName={hasInTheStory ? "entities-box" : ""}>
              <div styleName="aihub-frame1">
                {/* {heroImageUrl ? (
                  <div styleName="aihub-frame1-img">
                    <img src={heroImageUrl} alt={articleTitle} title={articleTitle} width={329} height={184} />
                  </div>
                ) : null} */}
                <div styleName="aihub-frame1-text">
                  {articleTitle}
                  {publishedLabel ? <small>{publishedLabel}</small> : null} {!isWebView && <SocialShareComponent />}
                </div>
              </div>
              {hasInTheStory && normalizedInTheStory.length > 0 ? (
                <div styleName="entities-box-list">
                  <div styleName="sidebar-label">In the story</div>
                  <ul styleName={`${normalizedInTheStory.length === 1 ? 'single-stat-list' : ''} ${statList ? 'stat-list-auto' : ''} stat-list`}>
                    {normalizedInTheStory.map((item, index) => (
                      <li key={`${item}-${index}`}><a href={item?.href} onClick={handleEntityClick}><span styleName="stat-val">{item?.name}</span><span styleName="stat-desc">{item?.rankType}{' '}{item?.rankYear}</span></a></li>
                    ))}
                  </ul>
                  {normalizedInTheStory.length > 3  && isDesktop && (<div styleName="list-more"><button aria-label="more list" styleName={`${statList ? 'open' : ''} list-down-arrow`} onClick={() => setStatList(prev => !prev)}></button></div>)}
                </div>
              ) : null}
            </div>
            {isLoading ? (
              <div styleName="aihub-empty-state">Loading AI Hub...</div>
            ) : !hasAiHubContent ? (
              <div styleName="aihub-empty-state">AI Hub is not available for this story yet.</div>
            ) : (
              <>
                {(hasSummary && summary) || (hasKeyTakeaways && normalizedTakeaways.length > 0) ? (
                  <div styleName={`${hasKeyTakeaways ? '' : 'has-key-takeaways'} aihub-frame2`}>
                    {hasSummary && summary ? (
                      <div styleName="aihub-summary">
                        <div styleName="aihub-summary-title">
                          <span>Summary</span>
                        </div>
                        <p>{summary}</p>
                      </div>
                    ) : null}
                    {hasKeyTakeaways && normalizedTakeaways.length > 0 ? (
                      <div styleName={`${hasSummary ? '' : 'has-summary'} aihub-section-frame`}>
                        <div styleName="aihub-section-title">Key Takeaways</div>
                        <ul styleName="aihub-listing">
                          {normalizedTakeaways.map((item, index) => (
                            <li key={`${item}-${index}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {hasTimeline && normalizedTimeline.length > 2 ? (
                  <div styleName="aihub-section-frame timeline-frame">
                    <div
                      ref={setAiHubScrollSectionRef("timeline")}
                      data-aihub-scroll-section="timeline"
                      styleName="aihub-section-title"
                    ><span>Timeline</span></div>
                    <div styleName="timeline-outer">
                      <div styleName="timeline">
                        {normalizedTimeline.map((item, index) => (
                          <div styleName="timeline-item" key={`${get(item, ["date"], "")}-${index}`}>

                            <div ref={(el) => (cardsRef.current[index] = el)} styleName="agenda-card">
                              <div styleName="agenda-time">{get(item, ["date"], "")} <div styleName="timeline-dot"></div></div>
                              <div>
                                <h3 styleName="agenda-title">{get(item, ["event"], "")}</h3>
                                <ul styleName="agenda-role">
                                  <li>{get(item, ["description"], "")}</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
                {hasFaqs && normalizedFaqs.length > 0 ? (
                  <div styleName="aihub-section-frame timeline-frame">
                    <div
                      ref={setAiHubScrollSectionRef("faqs")}
                      data-aihub-scroll-section="faqs"
                      styleName="aihub-section-title"
                    ><span>Frequently Asked Questions</span></div>
                    <div styleName="aihub-accordion-box">
                      {normalizedFaqs.map((faq, index) => (
                        <div styleName="aihub-accordion-item" key={get(faq, ["id"], index)}>
                          <button
                            type="button"
                            styleName="aihub-accordion-header"
                            aria-expanded={openFaqIndexes.includes(index)}
                            onClick={() =>
                              setOpenFaqIndexes((prev) =>
                                prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
                              )
                            }
                          >
                            <span>
                              <span styleName="question-mark">Q.</span> {get(faq, ["question"], "")}
                            </span>
                            {/*
                            <span styleName={`accordion-toggle ${openFaqIndexes.includes(index) ? "open" : ""}`}></span>
                            */}
                          </button>
                          {/* {openFaqIndexes.includes(index) ? ( */}
                          <div styleName="aihub-accordion-content">
                            <span styleName="answer-mark">A.</span> {get(faq, ["answer"], "")}
                          </div>
                          {/* ) : null} */}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {hasGlossary && normalizedGlossary.length > 0 ? (
                  <div styleName="aihub-section-frame timeline-frame">
                    <div
                      ref={(element) => {
                        firstPrinciplesRef.current = element;
                        setAiHubScrollSectionRef("glossary")(element);
                      }}
                      data-aihub-scroll-section="glossary"
                      styleName="aihub-section-title"
                    >
                      <span>First Principles</span>
                    </div>
                    <div styleName="aihub-definitions">
                      {normalizedGlossary?.slice(0, 6).map((item, index) => (
                        <div styleName="aihub-definition-item" key={`${get(item, ["term"], "")}-${index}`}>
                          <strong>{get(item, ["term"], "")}</strong>
                          <p>{get(item, ["definition"], "")}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
            {hasTweets && normalizedTweets.length > 0 ? (
              <div styleName="aihub-section-frame timeline-frame">
                <div
                  ref={setAiHubScrollSectionRef("social_reactions")}
                  data-aihub-scroll-section="social_reactions"
                  styleName="aihub-section-title"
                ><span>Social Reactions</span></div>
                <div styleName="social-frame-box">
                  {normalizedTweets.map((item, index) => (
                    <TwitterTweetEmbed
                      key={`${get(item, ["id"])}-${index}`}
                      tweetId={get(item, ["id"])}
                      options={{ width: 550, align: "center" }}
                    />
                  ))}
                </div>
              </div>
            ) : null}
            <div styleName="aihub-disclaimer"><strong
              ref={setAiHubScrollSectionRef("disclaimer")}
              data-aihub-scroll-section="disclaimer">Disclaimer :</strong> Certain content on this page, including summaries, timelines, FAQs, glossaries, highlights, insights, and other supplementary informational features, maybe generated or assisted by artificial intelligence tools. While reasonable efforts are made to review and verify such content, AI generated output may occasionally contain errors, omissions or inconsistencies. Readers are advised to independently verify any information before relying upon them for professional, legal, financial, medical or other decisions. The publisher along with its affiliates and contributors do not warrant accuracy of AI-generated content and disclaim any liability, loss or damage arising from its use.</div>
          </div>
        </div>
      </div>
    </>
  );
};

AIHub.defaultProps = {
  headline: "",
  aiHubScrollTarget: "",
  publishTime: 0,
  heroImageS3Key: "",
  summary: "",
  topic: "",
  keyTakeaways: [],
  timeline: [],
  faqs: [],
  glossary: [],
  tweets: [],
  inTheStory: [],
  isLoading: false,
  hasSummary: false,
  hasTopic: false,
  hasKeyTakeaways: false,
  hasTimeline: false,
  hasInTheStory: false,
  hasFaqs: false,
  hasGlossary: false,
  hasTweets: false,
  aiHubTemplate: false,
  shouldShowAiHubButton: false,
  shouldAutoOpenAiHub: true,
  storyInstanceId: "",
};

AIHub.propTypes = {
  aiHubClick: bool.isRequired,
  setAiHubClick: func.isRequired,
  aiHubScrollTarget: string,
  story: object.isRequired,
  qtConfig: object.isRequired,
  headline: string,
  publishTime: number,
  heroImageS3Key: string,
  summary: string,
  topic: string,
  keyTakeaways: array,
  inTheStory: array,
  timeline: array,
  faqs: array,
  glossary: array,
  isLoading: bool,
  hasSummary: bool,
  hasTopic: bool,
  hasKeyTakeaways: bool,
  hasTimeline: bool,
  hasInTheStory: bool,
  hasFaqs: bool,
  hasGlossary: bool,
  hasTweets: bool,
  tweets: array,
  aiHubTemplate: bool,
  shouldShowAiHubButton: bool,
  shouldAutoOpenAiHub: bool,
  storyInstanceId: string,
};

export default AIHub;
